/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    EMPTY_UTUT_PERMISSION_STATE,
    type UtutPermissionState,
} from '../../components/DashboardPages/ututDashboardTypes';
import authorizationService from '../../services/AuthorizationService';
import { DOMAIN_LCP } from '../../services/permissions/actionMapper';
import USER_ACTIONS_CP from '../../services/permissions/constants/actionsContentProduction';
import { getLocaleFromLanguageDropdownValue } from '../../utils/constants/content-production';

/** Playlist create permissions + allowed SAP codes; fetched once at CP load and on locale change. */
export interface PlaylistCreateState {
    permissions: Record<string, boolean>;
    playlistCreateAllowedSapCodes: string[] | undefined;
    loading: boolean;
    error: string | null;
    /** The locale (e.g. 'en_US') for which permissions were last fetched; null = not yet fetched. */
    fetchedForLocale: string | null;
}

/** Collection create permissions + allowed SAP codes; fetched once at CP load and on locale change. */
export interface CollectionCreateState {
    permissions: Record<string, boolean>;
    collectionCreateAllowedSapCodes: string[] | undefined;
    loading: boolean;
    error: string | null;
    /** The locale (e.g. 'en_US') for which permissions were last fetched; null = not yet fetched. */
    fetchedForLocale: string | null;
}

export type FormattedCpEntity = {
    aem_id: string;
    status: string;
    locale: string;
    applications?: { sap_code: string[]; fi_code: string[] };
};

/** Per-row permissions keyed by `${aem_id}%#%${status}`. */
export interface CpEntityRowPermissions {
    canEdit: boolean;
    canDelete: boolean;
}

export interface PlaylistRowPermissionsState {
    permissions: Record<string, CpEntityRowPermissions>;
}

export interface CollectionRowPermissionsState {
    permissions: Record<string, CpEntityRowPermissions>;
}

const initialPlaylistCreate: PlaylistCreateState = {
    permissions: {},
    playlistCreateAllowedSapCodes: undefined,
    loading: false,
    error: null,
    fetchedForLocale: null,
};

const initialCollectionCreate: CollectionCreateState = {
    permissions: {},
    collectionCreateAllowedSapCodes: undefined,
    loading: false,
    error: null,
    fetchedForLocale: null,
};

const initialPlaylistRowPermissions: PlaylistRowPermissionsState = {
    permissions: {},
};

const initialCollectionRowPermissions: CollectionRowPermissionsState = {
    permissions: {},
};

const initialUtutDashboardPermissions: UtutPermissionState = { ...EMPTY_UTUT_PERMISSION_STATE };

export interface AuthorizationState {
    playlistCreate: PlaylistCreateState;
    collectionCreate: CollectionCreateState;
    playlistRowPermissions: PlaylistRowPermissionsState;
    collectionRowPermissions: CollectionRowPermissionsState;
    ututDashboardPermissions: UtutPermissionState;
}

const initialState: AuthorizationState = {
    playlistCreate: initialPlaylistCreate,
    collectionCreate: initialCollectionCreate,
    playlistRowPermissions: initialPlaylistRowPermissions,
    collectionRowPermissions: initialCollectionRowPermissions,
    ututDashboardPermissions: initialUtutDashboardPermissions,
};

// ---------------------------------------------------------------------------
// Async thunks: fetch create capabilities once per locale, skip if already cached
// ---------------------------------------------------------------------------

type PlaylistCreateResult = {
    permissions: Record<string, boolean>;
    playlistCreateAllowedSapCodes?: string[];
    fetchedForLocale: string;
};

type CollectionCreateResult = {
    permissions: Record<string, boolean>;
    collectionCreateAllowedSapCodes?: string[];
    fetchedForLocale: string;
};

/**
 * Fetch playlist create capabilities for the given locale.
 * Skips the network call if permissions for this locale are already cached.
 * Dispatch from CPDashboardView on mount and whenever the dashboard locale changes.
 */
export const fetchPlaylistCreateCapability = createAsyncThunk<
    PlaylistCreateResult,
    { locale: string },
    { state: { authorization: AuthorizationState } }
>(
    'authorization/fetchPlaylistCreateCapability',
    async ({ locale }) => {
        const localeForAuth = getLocaleFromLanguageDropdownValue(locale);
        const result = await authorizationService.checkMultipleCapabilities(
            DOMAIN_LCP,
            [USER_ACTIONS_CP.DRAFT_CREATE_NEW_PL, USER_ACTIONS_CP.LIVE_CREATE_NEW_PL],
            { locale: localeForAuth },
        );
        return {
            permissions: result.permissions ?? {},
            playlistCreateAllowedSapCodes: result.playlistCreateAllowedSapCodes,
            fetchedForLocale: localeForAuth,
        };
    },
    {
        condition: ({ locale }, { getState }) => {
            const localeForAuth = getLocaleFromLanguageDropdownValue(locale);
            const { fetchedForLocale } = getState().authorization.playlistCreate;
            return fetchedForLocale !== localeForAuth;
        },
    },
);

/**
 * Fetch collection create capabilities for the given locale.
 * Skips the network call if permissions for this locale are already cached.
 * Dispatch from CPDashboardView on mount and whenever the collection locale changes.
 */
export const fetchCollectionCreateCapability = createAsyncThunk<
    CollectionCreateResult,
    { locale: string },
    { state: { authorization: AuthorizationState } }
>(
    'authorization/fetchCollectionCreateCapability',
    async ({ locale }) => {
        const localeForAuth = getLocaleFromLanguageDropdownValue(locale);
        const result = await authorizationService.checkMultipleCapabilities(
            DOMAIN_LCP,
            [USER_ACTIONS_CP.DRAFT_CREATE_NEW_CL, USER_ACTIONS_CP.LIVE_CREATE_NEW_CL],
            { locale: localeForAuth },
        );
        return {
            permissions: result.permissions ?? {},
            collectionCreateAllowedSapCodes: result.collectionCreateAllowedSapCodes,
            fetchedForLocale: localeForAuth,
        };
    },
    {
        condition: ({ locale }, { getState }) => {
            const localeForAuth = getLocaleFromLanguageDropdownValue(locale);
            const { fetchedForLocale } = getState().authorization.collectionCreate;
            return fetchedForLocale !== localeForAuth;
        },
    },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const authorizationSlice = createSlice({
    name: 'authorization',
    initialState,
    reducers: {
        updatePlaylistRowPermissions(state, action: PayloadAction<PlaylistRowPermissionsState>) {
            state.playlistRowPermissions = action.payload;
        },
        updateCollectionRowPermissions(state, action: PayloadAction<CollectionRowPermissionsState>) {
            state.collectionRowPermissions = action.payload;
        },
        updateUtutDashboardPermissions(state, action: PayloadAction<UtutPermissionState>) {
            state.ututDashboardPermissions = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Playlist create capability
        builder
            .addCase(fetchPlaylistCreateCapability.pending, (state) => {
                state.playlistCreate.loading = true;
                state.playlistCreate.error = null;
            })
            .addCase(fetchPlaylistCreateCapability.fulfilled, (state, action) => {
                state.playlistCreate.permissions = action.payload.permissions;
                state.playlistCreate.playlistCreateAllowedSapCodes = action.payload.playlistCreateAllowedSapCodes;
                state.playlistCreate.fetchedForLocale = action.payload.fetchedForLocale;
                state.playlistCreate.loading = false;
                state.playlistCreate.error = null;
            })
            .addCase(fetchPlaylistCreateCapability.rejected, (state, action) => {
                state.playlistCreate.loading = false;
                state.playlistCreate.error = action.error.message ?? 'Failed to fetch playlist create capability';
            });

        // Collection create capability
        builder
            .addCase(fetchCollectionCreateCapability.pending, (state) => {
                state.collectionCreate.loading = true;
                state.collectionCreate.error = null;
            })
            .addCase(fetchCollectionCreateCapability.fulfilled, (state, action) => {
                state.collectionCreate.permissions = action.payload.permissions;
                state.collectionCreate.collectionCreateAllowedSapCodes = action.payload.collectionCreateAllowedSapCodes;
                state.collectionCreate.fetchedForLocale = action.payload.fetchedForLocale;
                state.collectionCreate.loading = false;
                state.collectionCreate.error = null;
            })
            .addCase(fetchCollectionCreateCapability.rejected, (state, action) => {
                state.collectionCreate.loading = false;
                state.collectionCreate.error = action.error.message ?? 'Failed to fetch collection create capability';
            });
    },
});

export const { updatePlaylistRowPermissions, updateCollectionRowPermissions, updateUtutDashboardPermissions } =
    authorizationSlice.actions;
export default authorizationSlice.reducer;
